import { Link, useParams } from 'react-router-dom'
import { api, statsHref, type RoundSummary } from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { usePlayerParam, usePlayerPaths } from '../../components/playerPaths'
import { NotFound } from './NotFound'

/**
 * A player's profile, opened without signing in (US6 scenario 1, FR-047a).
 *
 * # The index is shown with what it is based on
 *
 * An index computed from four rounds is not the same claim as one computed from
 * twenty, and showing the number alone invites it to be read as the second. The
 * round count is beside it for that reason, not as a detail.
 *
 * # Best and worst are by DIFFERENTIAL, not by gross
 *
 * A 40 on a hard course and a 40 on an easy one are not the same round, and
 * ranking them by gross would call the easier one better. The differential is
 * the comparable figure — it is the whole reason it exists.
 */
export function Player() {
  const { pathFor, slugFor } = usePlayerPaths()
  const { playerId } = useParams()
  // The path carries a slug; the API is named by public id. `usePlayerParam`
  // accepts either, so a link written down before the change still resolves,
  // and rewrites the address bar to the readable form when it was a UUID.
  const resolved = usePlayerParam(playerId)
  const player = useApi(resolved ? (s) => api.player(resolved, s) : null, [resolved])
  const roster = useApi((s) => api.players(s), [])

  // Undefined is "the roster is still loading", null is "it loaded and names
  // nobody". Collapsing the two would show "no such player" during the request.
  if (resolved === undefined) return <Loading what="the player" />
  if (resolved === null) return <NotFound />
  if (player.state === 'loading') return <Loading what="the player" />
  if (player.state === 'error') return <Failed what="This player" error={player.error} />

  const p = player.data
  const others =
    roster.state === 'ready' ? roster.data.filter((o) => o.public_id !== p.public_id) : []

  return (
    <section>
      <h2>{p.display_name}</h2>

      <dl className="stat-grid">
        <div>
          <dt>Handicap index</dt>
          <dd className="stat-big">{p.current_index ?? '—'}</dd>
          <dd className="stat-note">
            {/* A declared index was not computed from rounds, so "from 0 rounds"
                would be true and misleading at once. */}
            {!p.current_index
              ? 'not enough rounds yet'
              : p.index_method === 'declared'
                ? 'declared starting index'
                : `from ${p.rounds_in_index} round${p.rounds_in_index === 1 ? '' : 's'}${
                    p.index_method === 'reduced' ? ', reduced record' : ''
                  }`}
          </dd>
        </div>
        {/* One per round length, over league rounds only. The league plays
            nines on regular weeks and eighteens on majors, and a single
            average over both is a number no round was ever near. */}
        {p.by_length.length === 0 ? (
          <div>
            <dt>Scoring average</dt>
            <dd className="stat-big">—</dd>
            <dd className="stat-note">no league rounds yet</dd>
          </div>
        ) : (
          p.by_length.map((l) => (
            <div key={l.holes} data-testid={`scoring-average-${l.holes}`}>
              <dt>Scoring average · {l.holes} holes</dt>
              <dd className="stat-big">{l.average}</dd>
              <dd className="stat-note">
                over {l.rounds} league round{l.rounds === 1 ? '' : 's'}
              </dd>
            </div>
          ))
        )}
        {p.season_standing && (
          <div>
            <dt>
              {p.season_id === null ? (
                'This season'
              ) : (
                <Link to={`${pathFor(p.public_id)}/seasons/${p.season_id}`}>This season</Link>
              )}
            </dt>
            <dd className="stat-big">{p.season_standing.points}</dd>
            <dd className="stat-note">
              {ordinal(p.season_standing.position)}
              {/* A stroke-play season has no matches, so every record is 0-0-0. */}
              {p.season_has_matches &&
                ` · ${p.season_standing.matches_won}-${p.season_standing.matches_lost}-${p.season_standing.matches_halved}`}
            </dd>
          </div>
        )}
      </dl>

      <h3>Rounds</h3>
      {p.by_length.length === 0 ? (
        <Empty>No league rounds recorded.</Empty>
      ) : (
        p.by_length.map((l) => (
          // Best and worst within ONE length: a nine-hole differential is on a
          // nine-hole basis, so ranked together the best would always be a nine.
          <div className="round-pair" key={l.holes} data-testid={`rounds-${l.holes}`}>
            <Round label={`Best ${l.holes}`} round={l.best} />
            <Round label={`Worst ${l.holes}`} round={l.worst} />
          </div>
        ))
      )}

      <h3>More</h3>
      <ul className="link-list">
        <li>
          <Link to={`${pathFor(p.public_id)}/handicap-trend`}>Handicap trend</Link>
        </li>
        <li>
          {/* Three clicks from the landing page to one player's numbers.
              Without it the statistics were reachable only by constructing a
              query string, which is not a route a member has (FR-039h).

              **Its own row.** Sharing the list item with the trend rendered
              them as one run-on word, "Handicap trendStatistics" — the link
              worked and was unreadable, which is the same as not being there. */}
          <Link to={statsHref(slugFor(p.public_id) ?? p.public_id)}>Statistics</Link>
        </li>
        {others.map((o) => (
          <li key={o.public_id}>
            <Link to={`${pathFor(p.public_id)}/head-to-head/${slugFor(o.public_id) ?? o.public_id}`}>
              Head to head v {o.display_name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Round({ label, round }: { label: string; round: RoundSummary | null }) {
  if (!round) {
    return (
      <div className="round-card">
        <h4>{label}</h4>
        <Empty>No rounds recorded.</Empty>
      </div>
    )
  }
  return (
    <div className="round-card">
      <h4>{label}</h4>
      <p className="stat-big">{round.gross_score}</p>
      <p className="stat-note">
        {round.holes_played} holes at {round.course} ({round.tee}) on{' '}
        <time dateTime={round.played_date}>{round.played_date}</time>
      </p>
      <p className="stat-note">Differential {round.differential}</p>
    </div>
  )
}

function ordinal(n: number): string {
  const suffix =
    n % 10 === 1 && n !== 11
      ? 'st'
      : n % 10 === 2 && n !== 12
        ? 'nd'
        : n % 10 === 3 && n !== 13
          ? 'rd'
          : 'th'
  return `${n}${suffix}`
}
